// The dashboard (UI kit W1): money in this month against last, what's
// pending and overdue, the last six months and the latest invoices. One
// currency at a time; amounts in different currencies are never added up.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { bankIsEmpty } from '@/core/business';
import { changeFromLastMonth, currenciesInUse, dashboardSummary, greetingFor, receivedThisMonth } from '@/core/dashboard';
import { today } from '@/core/dates';
import { currencyOf } from '@/core/money';
import { greetingLabel } from '@/l10n/labels';
import { t } from '@/l10n/strings';
import { InvoiceTable } from '@/pages/Invoices';
import { ButtonLink, EmptyArt, PageLoading, StatusChip } from '@/ui/controls';
import { Icon, type IconName } from '@/ui/Icon';

const longMonth = (month: number) => new Intl.DateTimeFormat('en', { month: 'long', timeZone: 'UTC' }).format(Date.UTC(2000, month - 1, 1));
const shortMonth = (month: number) => new Intl.DateTimeFormat('en', { month: 'short', timeZone: 'UTC' }).format(Date.UTC(2000, month - 1, 1));

export function DashboardPage() {
  const { invoices, business, clients, user } = useAppData();
  usePageTitle(t('navDashboard'));
  const fallback = currencyOf(business?.currency);
  const currencies = useMemo(() => (invoices ? currenciesInUse(invoices, fallback) : [fallback]), [invoices, fallback]);
  const [chosen, setChosen] = useState<string | null>(null);
  const currency = currencies.find((c) => c.code === chosen) ?? currencies[0];
  const day = today();
  const summary = useMemo(() => (invoices ? dashboardSummary(invoices, currency, day) : null), [invoices, currency, day]);

  if (!invoices || !business || !summary) return <PageLoading />;

  const name = business.name.trim() || t('appName');
  const greeting = greetingLabel(greetingFor(new Date().getHours()));
  const months = summary.months;
  const previous = months[months.length - 2];
  const change = changeFromLastMonth(summary);
  const peak = Math.max(1, ...months.map((m) => m.received.minor));
  const detailsAdded = business.logo !== null || business.address.trim() !== '' || !bankIsEmpty(business.bank);
  const clientAdded = (clients?.length ?? 0) > 0;

  return (
    <div className="page">
      <header className="page-head">
        <div className="page-head__title">
          <h1 className="display">
            {invoices.length === 0 ? t('homeLetsGetYouPaid') : `${greeting}, `}
            {invoices.length > 0 && <em>{name}</em>}
          </h1>
          <p className="body muted">{invoices.length === 0 ? t('homeWelcomeToPaidlark') : t('webDashboardSubtitle', { business: name })}</p>
        </div>
        <div className="page-head__actions">
          {currencies.length > 1 && (
            <div className="segmented" role="group" aria-label={t('homeCurrency')}>
              {currencies.map((c) => (
                <button key={c.code} type="button" aria-pressed={c.code === currency.code} onClick={() => setChosen(c.code)}>
                  {c.code}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {user?.isGuest && (
        <div className="banner banner--guest">
          <Icon name="cloudup" />
          <div className="banner__txt">
            <b>{t('homeYoureUsingPaidlarkAsA')}</b>
            <span>{t('webGuestBannerBody')}</span>
          </div>
          <ButtonLink to="/sign-in" variant="secondary" size="sm">
            {t('homeBackUp')}
          </ButtonLink>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div className="card empty enter">
            <EmptyArt />
            <h2 className="title-serif">{t('homeYourFirstInvoiceIsA')}</h2>
            <p className="body muted">{t('homeAddWhoYoureBillingAnd')}</p>
            <ButtonLink to="/invoices/new" variant="action" size="lg" icon="plus">
              {t('homeCreateAnInvoice')}
            </ButtonLink>
          </div>
          {(!detailsAdded || !clientAdded) && (
            <section className="stack-2 enter" style={{ ['--i' as string]: 1 }}>
              <h2 className="overline">{t('homeOrStartHere')}</h2>
              <div className="card card--tight">
                <StartStep to="/settings/profile" icon="store" title={t('homeAddYourLogoAndDetails')} detail={t('homeMakesInvoicesLookLikeYours')} done={detailsAdded} />
                <hr className="divider" />
                <StartStep to={clientAdded ? '/clients' : '/clients/new'} icon="userplus" title={t('homeAddYourFirstClient')} detail={t('homeReuseTheirDetailsOnEvery')} done={clientAdded} />
              </div>
            </section>
          )}
        </div>
      ) : (
        <>
          <section className="kpis" aria-label={t('webThisMonth')}>
            <div className="hero enter">
              <span className="hero__label">{t('homeReceivedIn', { month: longMonth(months[months.length - 1].month) })}</span>
              <b className="amount-hero" style={{ display: 'block', marginTop: 10, fontSize: 46, lineHeight: '50px' }}>
                {receivedThisMonth(summary).format()}
              </b>
              {change !== null && (
                <span className={`trend${change < 0 ? ' trend--flat' : ''}`} style={{ marginTop: 12 }}>
                  <Icon name={change < 0 ? 'in' : 'up'} size={14} stroke={2.4} />
                  {change === 0
                    ? t('homeSameAs', { month: longMonth(previous.month) })
                    : change > 0
                      ? t('homeMoreThan', { percent: Math.abs(change), month: longMonth(previous.month) })
                      : t('homeLessThan', { percent: Math.abs(change), month: longMonth(previous.month) })}
                </span>
              )}
            </div>
            <Link to="/invoices?filter=pending" className="kpi enter" style={{ ['--i' as string]: 1 }}>
              <StatusChip status="pending" />
              <b className="amount-lg">{summary.pending.format()}</b>
              <span className="caption">{t('homeInvoiceCount', { count: summary.pendingCount })}</span>
            </Link>
            <Link to="/invoices?filter=overdue" className="kpi enter" style={{ ['--i' as string]: 2 }}>
              <StatusChip status="overdue" />
              <b className="amount-lg">{summary.overdue.format()}</b>
              <span className="caption">{t('homeInvoiceCount', { count: summary.overdueCount })}</span>
            </Link>
            <Link to="/invoices?filter=draft" className="kpi enter" style={{ ['--i' as string]: 3 }}>
              <StatusChip status="draft" />
              <b className="amount-lg">{summary.draftCount}</b>
              <span className="caption">{t('webDraftsWaiting')}</span>
            </Link>
          </section>

          <div className="dash-grid">
            <section className="card stack-4 enter" style={{ ['--i' as string]: 4 }} aria-labelledby="cash-flow">
              <div className="stack-1">
                <h2 className="title-serif" id="cash-flow">
                  {t('webCashFlow')}
                </h2>
                <span className="caption">{t('webCashFlowDetail', { currency: currency.code })}</span>
              </div>
              <div className="chart" role="img" aria-label={months.map((m) => `${longMonth(m.month)}: ${m.received.format()}`).join(', ')}>
                {months.map((m, i) => (
                  <div className="chart__col" key={`${m.year}-${m.month}`} title={`${longMonth(m.month)} ${m.year}: ${m.received.format()}`}>
                    <span className="chart__value">{m.received.isZero ? '' : m.received.formatCompact()}</span>
                    <span className="chart__track">
                      <i className={`chart__bar${i === months.length - 1 ? ' chart__bar--now' : ''}`} style={{ height: `${Math.max(2, (m.received.minor / peak) * 100)}%`, animationDelay: `${0.25 + i * 0.06}s` }} />
                    </span>
                    <span className="chart__month">{shortMonth(m.month)}</span>
                  </div>
                ))}
              </div>
            </section>
            <section className="stack-2 enter" style={{ ['--i' as string]: 5 }} aria-labelledby="recent">
              <div className="between">
                <h2 className="title-serif" id="recent">
                  {t('homeRecentInvoices')}
                </h2>
                <Link to="/invoices" className="label">
                  {t('homeSeeAll')}
                </Link>
              </div>
              <InvoiceTable invoices={invoices.slice(0, 5)} compact />
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function StartStep({ to, icon, title, detail, done }: { to: string; icon: IconName; title: string; detail: string; done: boolean }) {
  return (
    <Link to={to} className="row" style={{ padding: '12px 4px', color: 'inherit', textDecoration: 'none' }}>
      <span className="tile">
        <Icon name={icon} size={18} />
      </span>
      <span className="grow stack-1">
        <b className="body-strong" style={done ? { textDecoration: 'line-through', color: 'var(--muted)' } : undefined}>
          {title}
        </b>
        <span className="caption">{detail}</span>
      </span>
      {done ? (
        <span className="label" style={{ color: 'var(--paid)' }}>
          {t('homeStepDone')}
        </span>
      ) : (
        <Icon name="chev" size={18} className="muted" />
      )}
    </Link>
  );
}
